/**
 * The two faces of a refused write (#877) — the toast and the inline statement.
 *
 * Both read from `classifyWrite`, which delegates to `QueryBoundary`'s
 * `classifyError`, so a 403 means the same thing on a read and on a write. The
 * split is the same one `QueryBoundary` already makes between the page-level
 * boundary and `<Unresolved>`: a toast for a change the user made from anywhere,
 * an inline notice next to the control that could not do it.
 */
import { useEffect, type ReactNode } from 'react'
import { ErrorState, useToast } from '@solum/ui'
import { classifyWrite, setWriteFailureSink } from './writeState'

/**
 * Registers the toast API as the process-wide write-failure sink.
 *
 * Renders nothing, and MUST sit inside <ToastProvider>. It exists because the
 * query client is built at module scope — outside React entirely — so the
 * mutation cache cannot call `useToast()`. One slot, set here, rather than the
 * data layer growing a second notification channel of its own.
 */
export function WriteFailureToaster(): ReactNode {
  const toast = useToast()
  useEffect(() => {
    setWriteFailureSink((f) => toast.error(f.title, f.message))
    return () => setWriteFailureSink(null)
  }, [toast])
  return null
}

/**
 * The refusal, said where the user was working.
 *
 * The retry rule is inherited rather than re-decided: `unauthorized` and
 * `not-installed` are non-retryable, so no retry affordance is offered for them
 * — the same reasoning as `QueryBoundary`'s unauthorized arm, which deliberately
 * does not draw a button that will fail identically.
 *
 * @param what a noun phrase for the thing that was not saved, e.g. "this order".
 */
export function WriteFailureNotice({ error, what, onRetry }: {
  error: unknown
  what?: string
  onRetry?: () => void
}): ReactNode {
  const f = classifyWrite(error, what)
  return (
    <div data-testid="write-failure" data-kind={f.kind} data-retryable={String(f.retryable)}>
      <ErrorState title={f.title} message={f.message} onRetry={f.retryable ? onRetry : undefined} />
    </div>
  )
}

/**
 * Why this record has no Save at all — the `write_door: 'rpc'` case.
 *
 * Deliberately NOT a `WriteFailureNotice`: nothing failed, and nothing was
 * attempted. Rendering a refusal for a door that was never opened is how a
 * user learns to ignore refusals.
 */
export function ReadOnlyNotice({ reason }: { reason: string }): ReactNode {
  return (
    <div data-testid="write-door-readonly" role="note">
      <ErrorState title="This record is read-only here" message={reason} />
    </div>
  )
}
